import { WhatsaapTemplate } from 'services/whatsaapTemplates/types';
import { TemplateListGroup } from './TemplateListGroup';
import { Channels } from 'generated-graphql/graphql';
import { TemplatedInput } from 'common/ui';
import { capitalizeFirstLetter } from 'utils/fomatting';
import { SMS_CUSTOM_MESSAGE_MAX_CHARACTERS } from '../constants';
import { FormikErrors, FormikTouched } from 'formik';
import { FormValues } from '../types';
import { getCampaignCreationTemplateVariables } from '../utils/getCampaignCreationTemplateVariables';
import { CampaignType } from 'services/campaign/types';
import { RadioGroup } from 'common/form';
import { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { organizationConfigAtom } from 'services/organization/atom';
import { OpeningMessageMode } from 'generated-graphql/types';
import { FieldWrapper } from 'common/form';

type Props = {
  isWhatsAppTemplatesLoading: boolean;
  templatesList: WhatsaapTemplate[];
  templateId: string;
  channel: Channels;
  onSelectTemplate: (template: WhatsaapTemplate) => void;
  isEdit: boolean;
  handleBlur: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  setFieldValue: (field: string, value: any) => void;
  customTemplatedMessage: string | null | undefined;
  customTemplatedSubjectLine: string | null | undefined;
  errors: FormikErrors<FormValues>;
  touched: FormikTouched<FormValues>;
  campaignType: CampaignType | null | undefined;
  isError: (field: string) => boolean;
};

export const OpeningMessage: React.FC<Props> = ({
  isWhatsAppTemplatesLoading,
  templatesList,
  templateId,
  onSelectTemplate,
  isEdit,
  channel,
  setFieldValue,
  customTemplatedMessage,
  customTemplatedSubjectLine,
  campaignType,
  handleBlur,
  touched,
  errors
}) => {
  const organizationConfig = useRecoilValue(organizationConfigAtom);
  const [viewModeSelected, setViewModeSelected] = useState<
    'templates_view' | 'custom_message_view' | null
  >(null);

  const openingMessageMode = organizationConfig?.openingMessageMode;
  const templateError = touched.templateId ? errors.templateId : undefined;

  useEffect(() => {
    if (viewModeSelected) return;

    // If the user has selected a template or custom message, we should keep the view mode selected
    if (templateId && viewModeSelected !== 'templates_view') {
      setViewModeSelected('templates_view');
      return;
    }

    if (customTemplatedMessage && viewModeSelected !== 'custom_message_view') {
      setViewModeSelected('custom_message_view');
      return;
    }
  }, [templateId, customTemplatedMessage, viewModeSelected]);

  const resetTemplate = useCallback(() => {
    if (!templateId) return;

    setFieldValue('templateId', null);
  }, [templateId, setFieldValue]);

  const resetCustomTemplatedMessage = useCallback(() => {
    if (!customTemplatedMessage) return;

    setFieldValue('customTemplatedMessage', null);
    setFieldValue('customTemplatedMessageCharactersCount', 0);
  }, [setFieldValue, customTemplatedMessage]);

  const templateListGroup = (
    <TemplateListGroup
      isLoading={isWhatsAppTemplatesLoading}
      templatesList={templatesList || []}
      templateIdSelected={templateId}
      onSelectTemplate={(template) => {
        onSelectTemplate(template);
        resetCustomTemplatedMessage();
      }}
      isEdit={!!isEdit}
      error={templateError}
    />
  );

  if (
    channel === Channels.Whatsapp ||
    (channel === Channels.Sms &&
      openingMessageMode === OpeningMessageMode.TemplatedOpeningMessage)
  ) {
    return (
      <FieldWrapper label="Choose opening message" labelError={!!templateError}>
        {templateListGroup}
      </FieldWrapper>
    );
  }

  const variables = getCampaignCreationTemplateVariables(campaignType).map(
    (variable) => ({
      label: capitalizeFirstLetter(variable.toLowerCase().replaceAll('_', ' ')),
      value: variable
    })
  );

  if (channel === Channels.Email) {
    return (
      <>
        <TemplatedInput
          id="customTemplatedSubjectLine"
          name="customTemplatedSubjectLine"
          onChange={(value) => {
            setFieldValue('customTemplatedSubjectLine', value);
          }}
          errorMessage={errors.customTemplatedSubjectLine}
          minHeight={80}
          field={{
            label: 'Subject line',
            availableVariables: variables,
            subLabel:
              'Personalise for multiple recipients: type @ or { and choose a variable'
          }}
          value={customTemplatedSubjectLine || ''}
        />
        <TemplatedInput
          id="customTemplatedMessage"
          name="customTemplatedMessage"
          onChange={(value, charactersCount) => {
            setFieldValue('customTemplatedMessage', value);
            setFieldValue(
              'customTemplatedMessageCharactersCount',
              charactersCount
            );
          }}
          onBlur={handleBlur}
          minHeight={300}
          errorMessage={errors.customTemplatedMessage}
          field={{
            label: 'Opening message',
            availableVariables: variables,
            subLabel: `▪ An opening message has been generated by Popp, which can be edited
▪ An unsubscribe link is automatically added at the end
▪ Personalise for multiple recipients: type @ or { and choose a variable
▪ Add your signature or disclaimer at the end`
          }}
          value={customTemplatedMessage || ''}
        />
      </>
    );
  }

  if (openingMessageMode === OpeningMessageMode.CustomOpeningMessage) {
    return (
      <FieldWrapper label="Opening message">
        <TemplatedInput
          id="customTemplatedMessage"
          name="customTemplatedMessage"
          onChange={(value, charactersCount) => {
            setFieldValue('customTemplatedMessage', value);
            setFieldValue(
              'customTemplatedMessageCharactersCount',
              charactersCount
            );
          }}
          field={{
            label: 'Opening message',
            availableVariables: variables,
            subLabel: `▪ An opening message has been generated by Popp, which can be edited
    ▪ You must include an opt-out message with the "STOP" keyword
    ▪ Personalise for multiple recipients: type @ or { and choose a variable`
          }}
          onBlur={handleBlur}
          errorMessage={
            errors.customTemplatedMessageCharactersCount ||
            errors.customTemplatedMessage
          }
          value={customTemplatedMessage || ''}
        />
      </FieldWrapper>
    );
  }

  const smsTemplatedInput = (
    <TemplatedInput
      id="customTemplatedMessage"
      name="customTemplatedMessage"
      onChange={(value, charactersCount) => {
        setFieldValue('customTemplatedMessage', value);
        setFieldValue('customTemplatedMessageCharactersCount', charactersCount);
      }}
      field={{
        label: 'Opening message',
        subLabel: `▪ An opening message has been generated by Popp, which can be edited
▪ You must include an opt-out message with the "STOP" keyword
▪ Personalise for multiple recipients: type @ or { and choose a variable`,
        availableVariables: variables
      }}
      charactersCountLimit={SMS_CUSTOM_MESSAGE_MAX_CHARACTERS}
      onBlur={handleBlur}
      errorMessage={
        errors.customTemplatedMessageCharactersCount ||
        errors.customTemplatedMessage
      }
      data-testid="custom-templated-message"
      value={customTemplatedMessage || ''}
    />
  );

  if (
    openingMessageMode === OpeningMessageMode.CustomOrTemplatedOpeningMessage
  ) {
    return (
      <Box>
        <RadioGroup
          field={{
            options: [
              { label: 'From templates', value: 'templates_view' },
              { label: 'Write your own', value: 'custom_message_view' }
            ]
          }}
          onChange={(value) => {
            const valueToSet = value as
              | 'templates_view'
              | 'custom_message_view';

            if (valueToSet === 'custom_message_view') {
              resetTemplate();
            }

            setViewModeSelected(valueToSet);
          }}
          value={viewModeSelected}
        />

        {viewModeSelected === 'templates_view' && templateListGroup}

        {viewModeSelected === 'custom_message_view' && smsTemplatedInput}
      </Box>
    );
  }

  return (
    <FieldWrapper label="Choose opening message">
      {templateListGroup}
    </FieldWrapper>
  );
};
